import { useState, useEffect, useCallback } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUser } from '../../types/cognito';

/**
 * Mirror the id token into one cookie on the club's domain, so kltk.se can
 * tell who is logged in to Volley. Only the id token - never the refresh
 * token, which stays in localStorage - so a reader learns the member's
 * identity for the token's lifetime and cannot extend the session. Off unless
 * both variables are set, which keeps localhost and any environment that has
 * not opted in exactly as before.
 */
const ID_COOKIE_NAME = process.env.REACT_APP_ID_COOKIE_NAME;
const ID_COOKIE_DOMAIN = process.env.REACT_APP_ID_COOKIE_DOMAIN;

const writeIdCookie = (idToken: string, expiresAtSeconds: number) => {
  if (!ID_COOKIE_NAME || !ID_COOKIE_DOMAIN) return;
  const expires = new Date(expiresAtSeconds * 1000).toUTCString();
  document.cookie = `${ID_COOKIE_NAME}=${idToken}; Domain=${ID_COOKIE_DOMAIN}; Path=/; Expires=${expires}; Secure; SameSite=Lax`;
};

const clearIdCookie = () => {
  if (!ID_COOKIE_NAME || !ID_COOKIE_DOMAIN) return;
  document.cookie = `${ID_COOKIE_NAME}=; Domain=${ID_COOKIE_DOMAIN}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
};

const syncIdCookie = async () => {
  if (!ID_COOKIE_NAME || !ID_COOKIE_DOMAIN) return;
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    writeIdCookie(idToken.getJwtToken(), idToken.getExpiration());
  } catch {
    clearIdCookie();
  }
};


const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState<CognitoUser | null> (null);
  const [isCoach , setIsCoach] = useState(false); 
  
  const checkAuthState = useCallback(async () => {
    setIsLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      setIsAuthenticated(true);
      syncIdCookie();
      
      setUserInfo(user)
      const userGroups = user.signInUserSession.idToken.payload['cognito:groups'] || [];
      if (userGroups.includes('admin')) {
        setIsAdmin(true); 
      } else if(userGroups.includes('coach')) {
        setIsCoach(true); 
      }
    } catch (error) {
      setIsAuthenticated(false);
      setIsAdmin(false); 
      clearIdCookie();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Amplify refreshes the tokens on its own; keep the cookie in step so the
  // homepage never holds an expired token while Volley is still signed in.
  useEffect(() => {
    if (!ID_COOKIE_NAME || !ID_COOKIE_DOMAIN) return;
    const stop = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh' || payload.event === 'signIn') syncIdCookie();
      if (payload.event === 'signOut') clearIdCookie();
    });
    return () => stop();
  }, []);

  const signOut = useCallback(async () => {
    try {
      await Auth.signOut();
      clearIdCookie();
      setIsAuthenticated(false);
      setIsAdmin(false);
      localStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  }, []);

  const recheckAuthentication = useCallback(() => {
    checkAuthState();
  }, [checkAuthState]);

  return { isAuthenticated, isLoading, isAdmin, isCoach, signOut, recheckAuthentication, userInfo };
};

export default useAuth;
